import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useNotifications } from '../../contexts/NotificationContext';

export default function NotificationBell() {
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>();

  // Calculer la position du dropdown sous le bouton cloche
  useEffect(() => {
    const updatePosition = () => {
      if (buttonRef.current && isOpen) {
        const rect = buttonRef.current.getBoundingClientRect();
        const isMobile = window.innerWidth < 640;

        if (isMobile) {
          setDropdownStyle({
            position: 'fixed',
            top: rect.bottom + 8,
            left: 8,
            right: 8,
            maxHeight: '80vh',
            zIndex: 9999,
          });
        } else {
          setDropdownStyle({
            position: 'fixed',
            top: rect.bottom + 8,
            right: Math.max(8, window.innerWidth - rect.right),
            width: 384,
            maxHeight: 600,
            zIndex: 9999,
          });
        }
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
    }

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  // Fermer le dropdown si on clique à l'extérieur (dropdown OU bouton)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleNotificationClick = async (notification: any) => {
    await markAsRead(notification.id);
    if (notification.lien) {
      navigate(notification.lien);
    }
    setIsOpen(false);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'suivi':
        return 'ri-heart-pulse-line';
      case 'rendez-vous':
        return 'ri-calendar-line';
      case 'prescription':
        return 'ri-medicine-bottle-line';
      case 'message':
        return 'ri-message-3-line';
      case 'rappel':
        return 'ri-alarm-line';
      case 'invitation':
        return 'ri-mail-line';
      default:
        return 'ri-notification-3-line';
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'suivi':
        return 'bg-blue-100 text-blue-600';
      case 'rendez-vous':
        return 'bg-green-100 text-green-600';
      case 'prescription':
        return 'bg-purple-100 text-purple-600';
      case 'message':
        return 'bg-yellow-100 text-yellow-600';
      case 'rappel':
        return 'bg-red-100 text-red-600';
      case 'invitation':
        return 'bg-teal-100 text-teal-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;

    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const dropdownContent = (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="bg-white rounded-2xl shadow-2xl border-2 border-gray-100 overflow-hidden flex flex-col"
    >
      {/* En-tête */}
      <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-clinic-blue to-clinic-green">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <i className="ri-notification-3-line"></i>
            Notifications
            {unreadCount > 0 && (
              <span className="bg-white text-clinic-blue text-xs px-2 py-1 rounded-full font-bold">
                {unreadCount}
              </span>
            )}
          </h3>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-xs text-white hover:text-gray-200 transition-colors whitespace-nowrap underline"
            >
              Tout marquer comme lu
            </button>
          )}
        </div>
      </div>

      {/* Liste des notifications */}
      <div className="overflow-y-auto flex-1">
        {notifications.length === 0 ? (
          <div className="p-8 text-center">
            <i className="ri-notification-off-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-500">Aucune notification</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                  !notification.lu ? 'bg-blue-50/50' : ''
                }`}
              >
                <div className="flex gap-3">
                  {/* Icône */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getNotificationColor(notification.type)}`}>
                    <i className={`${getNotificationIcon(notification.type)} text-lg`}></i>
                  </div>

                  {/* Contenu */}
                  <div className="flex-1 min-w-0" onClick={() => handleNotificationClick(notification)}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4 className={`text-sm font-semibold ${!notification.lu ? 'text-clinic-blue-dark' : 'text-gray-700'}`}>
                        {notification.titre}
                      </h4>
                      {!notification.lu && (
                        <span className="w-2 h-2 bg-clinic-blue rounded-full flex-shrink-0 mt-1"></span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                      {notification.message}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">
                        {formatDate(notification.date_creation)}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification.id);
                        }}
                        className="text-xs text-red-500 hover:text-red-700 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pied de page */}
      {notifications.length > 0 && (
        <div className="p-3 border-t border-gray-200 bg-gray-50 text-center">
          <button
            onClick={() => {
              setIsOpen(false);
            }}
            className="text-sm text-clinic-blue hover:text-clinic-green transition-colors font-medium whitespace-nowrap"
          >
            Voir toutes les notifications
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="relative">
      {/* Bouton cloche */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-700 hover:text-clinic-green transition-colors rounded-full hover:bg-gray-100"
      >
        <i className="ri-notification-3-line text-2xl"></i>
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown rendu dans un portail (hors du Header) */}
      {isOpen && createPortal(dropdownContent, document.body)}
    </div>
  );
}